import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import Icon, { ICON_OPTIONS } from "@/components/Icon";
import ShareGuests from "@/components/admin/ShareGuests";
import OwnerChecklist from "@/components/admin/OwnerChecklist";
import AdminCalendar from "@/components/admin/AdminCalendar";
import OwnersManager from "@/components/admin/OwnersManager";
import GuestCodes from "@/components/admin/GuestCodes";
import MaintenanceReports from "@/components/admin/MaintenanceReports";
import { Plus, Trash, SignOut, FloppyDisk, ArrowSquareOut, QrCode, ClipboardText, Key, Wrench, CalendarBlank, UsersThree } from "@phosphor-icons/react";

const inputCls =
  "rounded-none border-0 border-b border-border bg-transparent px-0 focus-visible:ring-0 focus-visible:border-accent";

function ItemsEditor({ items, onChange }) {
  const update = (idx, field, value) => {
    const next = items.map((it, i) => (i === idx ? { ...it, [field]: value } : it));
    onChange(next);
  };
  const add = () => onChange([...items, { title: "", meta: "", description: "", link: "" }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="cms-label text-foreground/50">List Items</Label>
        <button
          type="button"
          data-testid="add-item-btn"
          onClick={add}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-accent hover:opacity-70 transition-opacity"
        >
          <Plus size={14} weight="bold" /> Add item
        </button>
      </div>
      {items.map((it, idx) => (
        <div key={idx} className="border border-border p-4 space-y-3 bg-secondary/40" data-testid={`item-editor-${idx}`}>
          <div className="flex gap-3">
            <Input
              value={it.title}
              onChange={(e) => update(idx, "title", e.target.value)}
              placeholder="Name"
              className={inputCls + " flex-1"}
              data-testid={`item-title-${idx}`}
            />
            <button
              type="button"
              onClick={() => remove(idx)}
              data-testid={`item-remove-${idx}`}
              className="text-destructive/70 hover:text-destructive transition-colors"
            >
              <Trash size={18} />
            </button>
          </div>
          <Input
            value={it.meta}
            onChange={(e) => update(idx, "meta", e.target.value)}
            placeholder="Meta (e.g. Mexican • 10 min)"
            className={inputCls}
          />
          <Textarea
            value={it.description}
            onChange={(e) => update(idx, "description", e.target.value)}
            placeholder="Description"
            className="rounded-none border-border bg-transparent focus-visible:ring-accent min-h-[60px]"
          />
        </div>
      ))}
    </div>
  );
}

function PropertyEditor({ property, onSaved }) {
  const [form, setForm] = useState(property);
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/property", form);
      toast.success("Property details saved");
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="property-editor">
      <div>
        <span className="cms-label text-accent">Property</span>
        <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">Overview & Access</h2>
      </div>
      <Field label="Property Name">
        <Input value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="prop-name" />
      </Field>
      <Field label="Address">
        <Input value={form.address || ""} onChange={(e) => set("address", e.target.value)} className={inputCls} data-testid="prop-address" />
      </Field>
      <Field label="Tagline">
        <Input value={form.tagline || ""} onChange={(e) => set("tagline", e.target.value)} className={inputCls} />
      </Field>
      <Field label="Welcome Message">
        <Textarea value={form.welcome_message || ""} onChange={(e) => set("welcome_message", e.target.value)} className="rounded-none border-border bg-transparent focus-visible:ring-accent min-h-[100px]" data-testid="prop-welcome" />
      </Field>
      <Field label="Hero Image URL">
        <Input value={form.hero_image || ""} onChange={(e) => set("hero_image", e.target.value)} className={inputCls} />
      </Field>
      <p className="text-xs text-muted-foreground border-l-2 border-accent pl-4 py-1">
        Guest access codes are now managed under the <span className="text-foreground/70">Guidebook Logins</span> tab, so you can assign a different code to each guest or booking.
      </p>
      <SaveBar saving={saving} onSave={save} />
    </div>
  );
}

function SectionEditor({ section, onSaved, onDeleted }) {
  const [form, setForm] = useState(section);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(section), [section]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/sections/${section.key}`, form);
      toast.success(`"${form.title}" saved`);
      onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    try {
      await api.delete(`/admin/sections/${section.key}`);
      toast.success("Section deleted");
      onDeleted();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="section-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <span className="cms-label text-accent">{form.category}</span>
          <h2 className="font-display text-4xl tracking-tight text-foreground mt-2">{form.title || "Untitled"}</h2>
        </div>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="delete-section-btn" className="text-destructive/70 hover:text-destructive transition-colors mt-2">
              <Trash size={20} />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent className="bg-popover">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this section?</AlertDialogTitle>
              <AlertDialogDescription>
                &ldquo;{form.title}&rdquo; will be permanently removed from the guidebook.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={del} data-testid="confirm-delete-btn" className="bg-destructive hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <div className="grid grid-cols-2 gap-5">
        <Field label="Number">
          <Input value={form.number || ""} onChange={(e) => set("number", e.target.value)} className={inputCls} />
        </Field>
        <Field label="Category">
          <Input value={form.category || ""} onChange={(e) => set("category", e.target.value)} className={inputCls} />
        </Field>
      </div>
      <Field label="Title">
        <Input value={form.title || ""} onChange={(e) => set("title", e.target.value)} className={inputCls} data-testid="sec-title" />
      </Field>
      <Field label="Subtitle">
        <Input value={form.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} className={inputCls} />
      </Field>

      <div className="grid grid-cols-2 gap-5">
        <Field label="Type">
          <Select value={form.type} onValueChange={(v) => set("type", v)}>
            <SelectTrigger className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0" data-testid="sec-type">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover">
              <SelectItem value="text">Text</SelectItem>
              <SelectItem value="code">Code / Highlight</SelectItem>
              <SelectItem value="list">List (cards)</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Icon">
          <Select value={form.icon} onValueChange={(v) => set("icon", v)}>
            <SelectTrigger className="rounded-none border-0 border-b border-border bg-transparent px-0 focus:ring-0" data-testid="sec-icon">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-popover max-h-64">
              {ICON_OPTIONS.map((ic) => (
                <SelectItem key={ic} value={ic}>
                  <span className="flex items-center gap-2"><Icon name={ic} size={16} /> {ic}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
      </div>

      <Field label={form.type === "code" ? "Highlight (the code / value shown large)" : "Highlight / Callout"}>
        <Input value={form.highlight || ""} onChange={(e) => set("highlight", e.target.value)} className={inputCls} data-testid="sec-highlight" />
      </Field>

      {form.key === "wifi" && (
        <div className="grid grid-cols-2 gap-5 border border-border p-4 bg-secondary/40">
          <Field label="WiFi Network Name">
            <Input value={form.wifi_network || ""} onChange={(e) => set("wifi_network", e.target.value)} className={inputCls} data-testid="sec-wifi-network" />
          </Field>
          <Field label="WiFi Password">
            <Input value={form.wifi_password || ""} onChange={(e) => set("wifi_password", e.target.value)} className={inputCls} data-testid="sec-wifi-password" />
          </Field>
        </div>
      )}
      <Field label="Content (use line breaks; ALL-CAPS lines become sub-headings)">
        <Textarea value={form.content || ""} onChange={(e) => set("content", e.target.value)} className="rounded-none border-border bg-transparent focus-visible:ring-accent min-h-[180px] font-mono text-sm" data-testid="sec-content" />
      </Field>
      <Field label="Image URL (optional)">
        <Input value={form.image_url || ""} onChange={(e) => set("image_url", e.target.value)} className={inputCls} />
      </Field>

      {form.type === "list" && (
        <ItemsEditor items={form.items || []} onChange={(items) => set("items", items)} />
      )}

      <SaveBar saving={saving} onSave={save} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="cms-label text-foreground/50">{label}</Label>
      {children}
    </div>
  );
}

function SaveBar({ saving, onSave }) {
  return (
    <div className="pt-4 sticky bottom-0">
      <button
        onClick={onSave}
        disabled={saving}
        data-testid="save-btn"
        className="inline-flex items-center gap-2 bg-foreground text-primary-foreground pl-6 pr-7 py-3.5 rounded-full text-sm font-semibold hover:bg-accent transition-colors duration-300 disabled:opacity-60"
      >
        <FloppyDisk size={16} weight="bold" />
        {saving ? "Saving…" : "Save changes"}
      </button>
    </div>
  );
}

function AddSectionDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("General");

  const create = async () => {
    const slug = key.trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
    if (!slug || !title.trim()) {
      toast.error("Key and title are required");
      return;
    }
    try {
      await api.post("/admin/sections", {
        key: slug,
        title: title.trim(),
        category: category.trim() || "General",
        number: "",
        subtitle: "",
        content: "",
        image_url: "",
        icon: "House",
        type: "text",
        highlight: "",
        items: [],
        order: 999,
      });
      toast.success("Section created");
      setOpen(false);
      setKey("");
      setTitle("");
      onCreated(slug);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <button data-testid="add-section-btn" className="w-full inline-flex items-center justify-center gap-2 border border-dashed border-border py-3 text-sm text-muted-foreground hover:border-accent hover:text-accent transition-colors">
          <Plus size={16} weight="bold" /> Add section
        </button>
      </AlertDialogTrigger>
      <AlertDialogContent className="bg-popover">
        <AlertDialogHeader>
          <AlertDialogTitle>New section</AlertDialogTitle>
          <AlertDialogDescription>Give it a unique key and a title. You can edit everything else after.</AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-4">
          <Field label="Unique Key (slug)">
            <Input value={key} onChange={(e) => setKey(e.target.value)} placeholder="e.g. parking" className={inputCls} data-testid="new-section-key" />
          </Field>
          <Field label="Title">
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Parking Instructions" className={inputCls} data-testid="new-section-title" />
          </Field>
          <Field label="Category">
            <Input value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls} />
          </Field>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={create} data-testid="confirm-add-section" className="bg-foreground hover:bg-accent">Create</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

export default function AdminPanel() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState("__property__");
  const [navOpen, setNavOpen] = useState(false);

  const load = async (keepSelection = true) => {
    const { data } = await api.get("/admin/guidebook");
    setData(data);
    if (!keepSelection) return;
  };

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
    if (admin) load();
  }, [admin]); // eslint-disable-line react-hooks/exhaustive-deps

  if (admin === null || admin === false) {
    return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Loading…</div>;
  }
  if (!data) return <div className="min-h-screen grid place-items-center bg-background text-muted-foreground">Loading content…</div>;

  const { property, sections } = data;
  const current = sections.find((s) => s.key === selected);

  return (
    <div className="min-h-screen bg-background grain flex flex-col md:flex-row" data-testid="admin-panel">
      {/* Sidebar — top bar with collapsible menu on mobile, sticky sidebar on md+ */}
      <aside className="relative z-10 w-full md:w-72 shrink-0 border-b md:border-b-0 md:border-r border-border md:h-screen md:sticky md:top-0 flex flex-col">
        <div className="p-6 md:border-b border-border flex items-center justify-between gap-3">
          <div className="min-w-0">
            <span className="cms-label text-accent">Admin</span>
            <h1 className="font-display text-2xl tracking-tight text-foreground mt-1">Guidebook CMS</h1>
            <p className="text-xs text-muted-foreground mt-1 truncate">{admin.email}</p>
          </div>
          <button
            onClick={() => setNavOpen((o) => !o)}
            data-testid="admin-nav-toggle"
            className="md:hidden inline-flex items-center gap-2 border border-border px-4 py-2 rounded-full text-sm font-semibold shrink-0"
          >
            {navOpen ? "Close" : "Menu"}
          </button>
        </div>

        <nav
          className={(navOpen ? "block " : "hidden ") + "md:block flex-1 overflow-y-auto p-4 space-y-1 border-t md:border-t-0 border-border"}
          onClickCapture={() => setNavOpen(false)}
        >
          <button
            onClick={() => setSelected("__property__")}
            data-testid="nav-property"
            className={
              "w-full text-left px-3 py-2.5 text-sm transition-colors " +
              (selected === "__property__" ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
            }
          >
            Property & Access
          </button>

          <button
            onClick={() => setSelected("__share__")}
            data-testid="nav-share"
            className={
              "w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors " +
              (selected === "__share__" ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
            }
          >
            <QrCode size={16} /> Share QR Code
          </button>

          <button
            onClick={() => setSelected("__checklist__")}
            data-testid="nav-checklist"
            className={
              "w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors " +
              (selected === "__checklist__" ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
            }
          >
            <ClipboardText size={16} /> Departure Checklist
          </button>
          <button
            onClick={() => setSelected("__calendar__")}
            data-testid="nav-calendar"
            className={
              "w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors " +
              (selected === "__calendar__" ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
            }
          >
            <CalendarBlank size={16} /> Calendar
          </button>

          <button
            onClick={() => setSelected("__owners__")}
            data-testid="nav-owners"
            className={
              "w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors " +
              (selected === "__owners__" ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
            }
          >
            <UsersThree size={16} /> Owners
          </button>
          <button
            onClick={() => setSelected("__codes__")}
            data-testid="nav-codes"
            className={
              "w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors " +
              (selected === "__codes__" ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
            }
          >
            <Key size={16} /> Guidebook Logins
          </button>

          <button
            onClick={() => setSelected("__maintenance__")}
            data-testid="nav-maintenance"
            className={
              "w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors " +
              (selected === "__maintenance__" ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
            }
          >
            <Wrench size={16} /> Maintenance Reports
          </button>

          <p className="cms-label text-foreground/40 px-3 pt-5 pb-2">Sections</p>
          {sections.map((s) => (
            <button
              key={s.key}
              onClick={() => setSelected(s.key)}
              data-testid={`nav-section-${s.key}`}
              className={
                "w-full text-left px-3 py-2.5 text-sm flex items-center gap-2 transition-colors " +
                (selected === s.key ? "bg-foreground text-primary-foreground" : "hover:bg-secondary text-foreground")
              }
            >
              <span className="text-xs opacity-50 w-5">{s.number || "–"}</span>
              <span className="truncate">{s.title}</span>
            </button>
          ))}

          <div className="pt-3">
            <AddSectionDialog onCreated={(slug) => load().then(() => setSelected(slug))} />
          </div>
        </nav>

        <div className={(navOpen ? "block " : "hidden ") + "md:block p-4 border-t border-border space-y-2"}>
          <a href="/" target="_blank" rel="noreferrer" data-testid="view-live-btn" className="w-full inline-flex items-center justify-center gap-2 text-xs text-muted-foreground hover:text-accent transition-colors py-2">
            <ArrowSquareOut size={14} /> View live guidebook
          </a>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn" className="w-full inline-flex items-center justify-center gap-2 border border-border py-2.5 text-sm hover:bg-secondary transition-colors">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Editor */}
      <main className="relative z-10 flex-1 p-5 sm:p-8 md:p-14 overflow-y-auto">
        {selected === "__property__" ? (
          <PropertyEditor property={property} onSaved={() => load()} />
        ) : selected === "__share__" ? (
          <ShareGuests property={property} onSaved={() => load()} />
        ) : selected === "__checklist__" ? (
          <OwnerChecklist property={property} />
        ) : selected === "__calendar__" ? (
          <AdminCalendar />
        ) : selected === "__codes__" ? (
          <GuestCodes />
        ) : selected === "__owners__" ? (
          <OwnersManager currentEmail={admin?.email} />
        ) : selected === "__maintenance__" ? (
          <MaintenanceReports />
        ) : current ? (
          <SectionEditor
            key={current.key}
            section={current}
            onSaved={() => load()}
            onDeleted={() => load().then(() => setSelected("__property__"))}
          />
        ) : (
          <p className="text-muted-foreground">Select a section to edit.</p>
        )}
      </main>
    </div>
  );
}
